import { useEffect, useState } from "react";
import { Alert, Button, Flex, Form, Modal, message } from "antd";

import { PostCase } from "../../../api/postCaseAPI";
import { USE_MOCK_VIDEO, VIDEO_EXTENSIONS, postVideo } from "../../../api/videoAPI";
import CaseFileGroups, {
  CaseFileGroup,
  UNASSIGNED_VALUE,
  UPLOAD_MODAL_BODY_STYLE,
  createCaseFileGroup,
  findDuplicateAcrossGroups,
  flattenCaseFileGroups,
} from "./CaseFileGroups";
import UploadProgressModal, { useUploadProgress } from "../../upload/UploadProgressModal";

interface Props {
  postId: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cases?: PostCase[];
  defaultCaseId?: number | null;
  /** 업로드가 끝난 뒤 목록 새로고침 */
  onUploaded?: () => Promise<void> | void;
}

/** 동영상 업로드 모달 (이미지 업로드와 같은 Case 별 묶음 방식) */
const AddVideo = ({ postId, open, setOpen, cases = [], defaultCaseId = null, onUploaded }: Props) => {
  const [form] = Form.useForm();
  const [caseGroups, setCaseGroups] = useState<CaseFileGroup[]>(() => [createCaseFileGroup()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { progress, start, startFile, updateFile, finish } = useUploadProgress();

  useEffect(() => {
    if (open) setCaseGroups([createCaseFileGroup(defaultCaseId ?? UNASSIGNED_VALUE)]);
  }, [open, defaultCaseId]);

  const close = () => {
    setCaseGroups([createCaseFileGroup()]);
    setOpen(false);
  };

  const upload = async () => {
    if (isSubmitting) return;
    if (USE_MOCK_VIDEO) {
      message.info("동영상 업로드 API 가 아직 준비되지 않아 실제로 업로드되지 않습니다.");
      return;
    }
    const entries = flattenCaseFileGroups(caseGroups);
    if (entries.length === 0) {
      message.error("Please upload a file");
      return;
    }
    const duplicated = findDuplicateAcrossGroups(caseGroups);
    if (duplicated) {
      message.error(`${duplicated}: 여러 Case 에 같은 파일이 들어 있습니다.`);
      return;
    }

    setIsSubmitting(true);
    start(entries.length);
    let failCount = 0;
    let lastErrorMsg = "";

    for (let i = 0; i < entries.length; i++) {
      const { item: fileItem, postCaseId } = entries[i];
      startFile(i, fileItem.name);
      const result = await postVideo(
        { postId, postCaseId, file: fileItem.originFileObj },
        (ratio) => updateFile(i, ratio)
      );
      if (!result.success) {
        failCount++;
        lastErrorMsg = result.message || "";
      }
    }

    await onUploaded?.();
    finish();
    setIsSubmitting(false);

    if (failCount > 0) {
      message.error(
        `${entries.length}개 중 ${failCount}개 파일 업로드에 실패했습니다.${lastErrorMsg ? ` (${lastErrorMsg})` : ""}`
      );
    } else {
      message.success(`${entries.length}개의 동영상이 등록되었습니다.`);
    }
    close();
  };

  return (
    <>
      <Modal
        centered
        title="Upload Video"
        styles={{ body: UPLOAD_MODAL_BODY_STYLE }}
        open={open}
        onCancel={close}
        footer={null}
        destroyOnClose
      >
        <Flex gap="middle" vertical>
          {USE_MOCK_VIDEO && (
            <Alert
              type="warning"
              showIcon
              message="동영상 API 연결 전 화면입니다. 업로드는 아직 동작하지 않습니다."
            />
          )}
          <Form form={form} onFinish={upload} layout="vertical">
            <CaseFileGroups
              cases={cases}
              groups={caseGroups}
              setGroups={setCaseGroups}
              mode="all"
              allowedExtensions={VIDEO_EXTENSIONS}
              enableScroll={false}
            />
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={close} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
      <UploadProgressModal {...progress} />
    </>
  );
};

export default AddVideo;
